import React, { useState, useCallback } from 'react';
import { Plus, X, RotateCcw } from 'lucide-react';
import { visiteAPI } from '../../services/api';
import { useSalvaCampo } from '../../utils/salvaCampo';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';
import { notificaErrore, notificaConferma, messaggioDa } from '../../utils/notifiche';
import '../../styles/ProdottiProcedura.css';

/**
 * ProdottiProcedura — i prodotti di UNA procedura iniettabile (filler, Alex
 * 7/10/2026; migration 324).
 *
 * L'elenco accanto allo schema delle zone: ogni prodotto col suo colore, i
 * millilitri e il lotto quando la merce e' uscita. Si aggiunge, si cambiano
 * i ml, si toglie. Il magazzino segue da solo (il database): lo scarico del
 * prodotto aggiunto, il reso e lo scarico nuovo quando cambiano i ml.
 *
 * CHI E QUANDO LO DICE IL SERVER: il medico della visita, il giorno stesso
 * (`procedura.modificabile`, col perche' quando no). Qui non se ne fa una
 * copia: senza il permesso l'elenco e' in sola lettura.
 *
 * I ml si salvano all'uscita dal campo (regola di Alex del 24/09/2026), con
 * la stessa coda dei campi del riquadro; un valore rifiutato resta nel campo,
 * rosso. Aggiungere e togliere sono gesti, e restano pulsanti.
 */

/** 0.5 -> «0,5»: i ml come li scrive chi li legge. */
export const mlLeggibili = (ml) => {
    const n = Number(ml);
    if (!Number.isFinite(n)) return '';
    return String(Math.round(n * 100) / 100).replace('.', ',');
};

/** Cosa sa il magazzino di quel prodotto, in parole. */
const statoMagazzino = (r) => {
    if (r.lotti) return `lotto ${r.lotti}`;
    if (r.in_attesa === 'lotto da leggere') return 'lotto da leggere (lo scrive la segreteria dal tagliando)';
    if (r.in_attesa === 'senza scorta') return 'senza scorta in magazzino';
    if (r.in_attesa === 'da confermare') return 'scarico da confermare in segreteria';
    return '';
};

const ProdottiProcedura = ({ rigaId, righe, procedura, onCambiati }) => {
    const modificabile = !!procedura?.modificabile;
    const attivi = (righe || []).filter((r) => !r.tolto);
    const tolti = (righe || []).filter((r) => r.tolto);

    const [testi, setTesti] = useState({});            // id -> testo dei ml mentre si scrive
    const [aggiungendo, setAggiungendo] = useState(false);
    const [candidati, setCandidati] = useState(null);
    const [scelto, setScelto] = useState('');
    const [mlNuovi, setMlNuovi] = useState('');
    const [inCorso, setInCorso] = useState(false);

    const scrivi = useCallback(async (campo, valore) => {
        const id = Number(campo.replace('ml_', ''));
        try {
            await visiteAPI.mlProdotto(id, valore);
            setTesti((t) => { const x = { ...t }; delete x[id]; return x; });
            if (onCambiati) onCambiati();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare i ml'));
            throw e;
        }
    }, [onCambiati]);
    const { stato, salva, nonSalvati } = useSalvaCampo(scrivi);
    useUscitaProtetta(nonSalvati.length > 0, nonSalvati.length);

    const apriAggiungi = async () => {
        setAggiungendo(true);
        setScelto('');
        setMlNuovi('');
        setCandidati(null);
        try {
            const r = await visiteAPI.prodottiCandidati(rigaId);
            setCandidati(r.data?.data?.prodotti || []);
        } catch (e) {
            setCandidati([]);
            notificaErrore(messaggioDa(e, 'Non riesco a leggere i prodotti iniettabili'));
        }
    };

    const aggiungi = async (prodottoId, ml) => {
        setInCorso(true);
        try {
            const r = await visiteAPI.aggiungiProdotto(rigaId, prodottoId, ml);
            notificaConferma(r.data?.message || 'Prodotto aggiunto alla procedura.');
            setAggiungendo(false);
            if (onCambiati) onCambiati();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco ad aggiungere il prodotto'));
        } finally {
            setInCorso(false);
        }
    };

    const togli = async (r) => {
        if (!window.confirm(`Togli «${r.prodotto}» dalla procedura?\n\n`
            + 'Se era uscito dal magazzino torna con un reso. Resta nella storia della procedura, '
            + 'e lo puoi rimettere oggi.')) return;
        setInCorso(true);
        try {
            const x = await visiteAPI.togliProdotto(r.id);
            notificaConferma(x.data?.message || 'Prodotto tolto dalla procedura.');
            if (onCambiati) onCambiati();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a togliere il prodotto'));
        } finally {
            setInCorso(false);
        }
    };

    const rimetti = (r) => {
        const ml = window.prompt(`Quanti ml di «${r.prodotto}»?`, mlLeggibili(r.ml));
        if (ml === null) return;
        aggiungi(r.prodotto_id, ml);
    };

    return (
        <div className="pp-wrap">
            {attivi.length === 0 ? (
                <p className="pp-vuoto">
                    {modificabile
                        ? 'Nessun prodotto: aggiungi quelli che inietti, poi segnali sullo schema.'
                        : 'Nessun prodotto registrato.'}
                </p>
            ) : (
                /* La tabella scorre nel suo contenitore (.rm-scorre, tema.css): a
                   390 px era 333 px in un riquadro di 242, e «Togli prodotto»
                   restava fuori, irraggiungibile (rilievo del beta, 12/10). */
                <div data-salva-da-solo className="rm-scorre">
                    <table className="pp-tabella">
                        <thead>
                            <tr>
                                <th aria-label="Colore" />
                                <th>Prodotto</th>
                                <th className="text-right">ml</th>
                                <th>Magazzino</th>
                                {modificabile && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {attivi.map((r) => {
                                const campo = `ml_${r.id}`;
                                const testo = testi[r.id] ?? mlLeggibili(r.ml);
                                return (
                                    <tr key={r.id} data-prodotto-procedura={r.prodotto_id}>
                                        <td><span className="pp-colore" style={{ background: r.colore }} /></td>
                                        <td>
                                            {r.prodotto}
                                            {r.origine === 'distinta' && <span className="pp-sub"> · dalla voce</span>}
                                        </td>
                                        <td className="text-right">
                                            {modificabile ? (
                                                <input className={`pp-ml ${stato[campo] === 'non_salvato' ? 'pp-ml-rosso' : ''}`}
                                                       value={testo} inputMode="decimal" aria-label={`ml di ${r.prodotto}`}
                                                       onChange={(e) => setTesti((t) => ({ ...t, [r.id]: e.target.value }))}
                                                       onBlur={() => {
                                                           if (testi[r.id] !== undefined && testi[r.id] !== mlLeggibili(r.ml)) {
                                                               salva(campo, testi[r.id]);
                                                           }
                                                       }} />
                                            ) : mlLeggibili(r.ml)}
                                            {stato[campo] === 'salvato' && <span className="pp-sub"> salvato</span>}
                                        </td>
                                        <td className="pp-magazzino">{statoMagazzino(r)}</td>
                                        {modificabile && (
                                            <td>
                                                <button type="button" className="pp-togli" disabled={inCorso}
                                                        onClick={() => togli(r)} title="Togli prodotto">
                                                    <X size={14} aria-hidden="true" /> Togli prodotto
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {tolti.length > 0 && (
                <p className="pp-tolti">
                    Tolti:{' '}
                    {tolti.map((r, i) => (
                        <span key={r.id}>
                            {i > 0 && ', '}
                            {r.prodotto}
                            {modificabile && (
                                <button type="button" className="pp-rimetti" disabled={inCorso}
                                        onClick={() => rimetti(r)} title="Rimetti il prodotto nella procedura">
                                    <RotateCcw size={12} aria-hidden="true" /> Rimetti
                                </button>
                            )}
                        </span>
                    ))}
                </p>
            )}

            {modificabile && !aggiungendo && attivi.length < 8 && (
                <button type="button" className="pd-add pp-aggiungi" onClick={apriAggiungi}>
                    <Plus size={13} aria-hidden="true" /> Aggiungi prodotto
                </button>
            )}
            {modificabile && aggiungendo && (
                <div className="pp-nuovo" role="group" aria-label="Aggiungi prodotto">
                    {candidati === null ? <span className="pp-sub">Lettura…</span> : candidati.length === 0 ? (
                        <span className="pp-sub">
                            Nessun altro prodotto iniettabile di questo tipo in magazzino: lo dichiara la
                            segreteria, nella scheda del prodotto.
                        </span>
                    ) : (
                        <>
                            <select value={scelto} onChange={(e) => setScelto(e.target.value)} aria-label="Prodotto">
                                <option value="">Scegli il prodotto…</option>
                                {candidati.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.nome}{c.tracciabilita_rigorosa ? ' (lotto obbligatorio)' : ''}
                                    </option>
                                ))}
                            </select>
                            <input className="pp-ml" value={mlNuovi} inputMode="decimal" placeholder="ml"
                                   aria-label="ml" onChange={(e) => setMlNuovi(e.target.value)} />
                            <button type="button" className="pd-add" disabled={!scelto || !mlNuovi.trim() || inCorso}
                                    onClick={() => aggiungi(Number(scelto), mlNuovi)}>
                                Aggiungi
                            </button>
                        </>
                    )}
                    <button type="button" className="pp-annulla" onClick={() => setAggiungendo(false)}>
                        Annulla
                    </button>
                </div>
            )}
            {!modificabile && procedura?.rifiuto && attivi.length > 0 && (
                <p className="pp-sub">{procedura.rifiuto}</p>
            )}
        </div>
    );
};

export default ProdottiProcedura;
